import { CloseOutlined } from "@ant-design/icons";
import { DataImport, DataSourceType, LookerFetchMode, LookerParams } from "@coeff/api";
import { AdminExtDataImportRun } from "@coeff/api-admin-ext";
import { CurvedNoArrow } from "@coeff/components";
import { Button } from "antd";
import React from "react";
import styled from "styled-components";

import { COLORS, SQL_DATASOURCES } from "../constants";
import GmailImportDetails from "../routes/Dashboard/routes/pages/GmailImportDetails";

import {
  Card,
  CardHeader,
  Columns,
  DEPRECATED_Scheduler,
  Filter as FilterIcon,
  FiltersAndSorts,
  SelectedColumns,
  Separator,
  SourceFile,
  SQL as SQLIcon,
  FlexColumn,
  TextWithIcon,
  Typography,
  SettingsBars,
  Paper,
  PaperContent,
  FlexRow,
} from ".";

const SQLQueryWrap = styled.div`
  margin-left: 25px;
  color: ${COLORS.black65};
  font-weight: bold;
  font-family: "Courier New", monospace;
  position: relative;
  overflow: hidden;
  max-height: 300px;
  min-height: 95px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  &:after {
    content: "";
    position: absolute;
    bottom: 0px;
    left: 0px;
    right: 0px;
    height: 50px;
    background-image: linear-gradient(transparent, ${COLORS.coeblue});
  }
`;

// CardHeader's flex `.title` defaults to min-width:auto, so a long unbreakable
// SQL table name expands the header and overflows the panel. min-width:0 lets it
// shrink so the inner Typography can ellipsize instead. (ENG-7771)
const TruncatingCardHeader = styled(CardHeader)`
  .title {
    min-width: 0;
  }
`;

type Props = {
  run: AdminExtDataImportRun;
  onClose?: () => void;
};

export const ImportRunDetailsSidebarContent: React.FC<Props> = ({ run, onClose }) => {
  const getSelectedObjectText = (): React.ReactNode => {
    if (
      run?.data_source_type === "salesforce" &&
      run?.source_info?.salesforce_fetch_mode === "objects"
    ) {
      return `Object: ${run?.source_info?.salesforce_object_name}`;
    }
    if (
      run?.data_source_type === "salesforce" &&
      run?.source_info?.salesforce_fetch_mode === "reports"
    ) {
      return `Report: ${run?.source_info?.salesforce_report_name}`;
    }
    if (run?.data_source_type === "hubspot") {
      return `Object: ${run?.source_info?.hubspot_object_name}`;
    }

    if (
      SQL_DATASOURCES.includes(run?.data_source_type ?? "") &&
      !run?.source_info?.sql_is_raw_query_mode
    ) {
      return `Table: ${run?.source_info?.sql_table_name}`;
    }

    if (run?.data_source_type === DataSourceType.Looker) {
      // Looker's config lives in the untyped ppress_params blob rather than as
      // flattened source_info fields, so narrow it to LookerParams here. On a run
      // this comes from the run's own data_import_json snapshot, so it reflects
      // the config as it was when the run executed.
      const lookerParams = (run?.source_info?.ppress_params ?? {}) as LookerParams;

      if (lookerParams.fetch_mode === LookerFetchMode.Looks) {
        return lookerParams.looker_look
          ? `Look: ${lookerParams.looker_look.look_name}`
          : lookerParams.looker_dashboard_element
          ? `Dashboard Element: ${lookerParams.looker_dashboard_element.dashboard_element_name}`
          : null;
      }

      if (lookerParams.fetch_mode === LookerFetchMode.ExploreUrl) {
        return lookerParams.looker_explore_url ?? null;
      }

      if (lookerParams.selected_model || lookerParams.selected_explore) {
        return `Model: ${lookerParams.selected_model ?? "-"}`;
      }

      return null;
    }

    return null;
  };

  const getLookerExploreText = (): string | null => {
    if (run?.data_source_type !== DataSourceType.Looker) {
      return null;
    }

    const lookerParams = (run?.source_info?.ppress_params ?? {}) as LookerParams;

    if (lookerParams.fetch_mode !== LookerFetchMode.Objects) {
      return null;
    }

    return lookerParams.selected_model || lookerParams.selected_explore
      ? `Explore: ${lookerParams.selected_explore ?? "-"}`
      : null;
  };

  const selectedObject = getSelectedObjectText();

  const lookerExplore = getLookerExploreText();

  const isNetSuiteCrx = run?.data_source_type === "netsuite_crx";

  const isRawQueryMode =
    (run?.data_source_type === "salesforce" &&
      run.source_info?.salesforce_objects_is_raw_query_mode) ||
    (SQL_DATASOURCES.includes(run?.data_source_type ?? "") &&
      run?.source_info?.sql_is_raw_query_mode);

  const dataImportPartial = {
    data_source_type: run?.data_source_type,
    source_info: run?.source_info,
    columns: run?.columns,
    filters: run?.filters,
    filter_clause: run?.filter_clause,
    sorts: run?.sorts,
  } as DataImport;

  return (
    <div style={{ overflowY: "auto" }}>
      <div style={{ margin: 15, marginBottom: 20 }}>
        <FlexColumn>
          <FlexRow style={{ justifyContent: "space-between" }}>
            <TextWithIcon
              title={
                <Typography variant="body1" fontWeight={700}>
                  Settings
                </Typography>
              }
              icon={<SettingsBars />}
            />

            {onClose ? (
              <Button type="text" onClick={onClose}>
                <CloseOutlined />
              </Button>
            ) : null}
          </FlexRow>

          <Paper variant="info">
            <PaperContent size="xsmall">
              <Typography color={COLORS.black65}>
                <Typography fontWeight={600}>READ-ONLY</Typography>
                Open in spreadsheet to make changes.
              </Typography>
            </PaperContent>
          </Paper>
        </FlexColumn>
      </div>

      <div style={{ position: "relative", opacity: "0.5" }}>
        <DEPRECATED_Scheduler
          schedule={run?.options?.schedule as any}
          snapshot_schedule={run?.options?.snapshot_schedule as any}
          ss_id=""
        />

        {run?.data_source_type === DataSourceType.Gmail ? (
          <GmailImportDetails dataImport={dataImportPartial} />
        ) : (
          <>
            {!isNetSuiteCrx && (
              <Card className="coeblue coeff__padded">
                <div>
                  {selectedObject && (
                    <>
                      <TruncatingCardHeader
                        title={<Typography isEllipsedContentTooltip>{selectedObject}</Typography>}
                        icon={<SourceFile fill={COLORS.black85} />}
                      />
                      {lookerExplore && (
                        <TruncatingCardHeader
                          title={<Typography isEllipsedContentTooltip>{lookerExplore}</Typography>}
                          icon={
                            <CurvedNoArrow
                              style={{ width: 10, margin: "0px 6px" }}
                              fill={COLORS.black85}
                            />
                          }
                        />
                      )}
                      <Separator />
                    </>
                  )}

                  {!isRawQueryMode && (
                    <>
                      <CardHeader
                        title={`${(run?.columns ?? []).length || (run?.num_columns ?? 0)} fields`}
                        icon={<Columns />}
                      />
                      <div>
                        <SelectedColumns columns={(run?.columns ?? []) as any} />
                      </div>
                    </>
                  )}

                  {isRawQueryMode && (
                    <>
                      <CardHeader
                        title={`Custom S${
                          run?.data_source_type === "salesforce" ? "O" : ""
                        }QL Query`}
                        icon={<SQLIcon />}
                      />
                      <SQLQueryWrap>
                        {run?.source_info?.sql_raw_query ?? run?.source_info?.salesforce_soql_query}
                      </SQLQueryWrap>
                    </>
                  )}
                </div>
              </Card>
            )}

            {!isRawQueryMode && (
              <Card className="coeff__padded">
                <div>
                  <CardHeader title="Filter and Sort" icon={<FilterIcon />} />
                  <FiltersAndSorts dataImport={dataImportPartial} />
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};
